import { AddressDisplay, Tooltip } from "@gotts.ai/ui";
import { useNavigate } from "react-router-dom";

import { useAddressResolver } from "../hooks/useAddressResolver";

import type { Address as AddressType } from "viem";

interface AddressProps {
  address: AddressType;
  leadChars?: number;
  tailChars?: number;
  fontSize?: number;
  className?: string;
  /** When true, resolved addresses become clickable links to the relevant page. */
  navigable?: boolean;
}

/**
 * Devenv-standard address display: 6 lead + 4 tail chars, full address in tooltip, copy on click.
 * With navigable=true, resolved addresses (accounts, tokens, contracts) are clickable.
 */
export function Address({
  address,
  leadChars = 6,
  tailChars = 4,
  fontSize = 11,
  className,
  navigable = false,
}: AddressProps) {
  const navigate = useNavigate();
  const { resolveAddress } = useAddressResolver();
  const resolved = navigable ? resolveAddress(address) : null;

  const inner = (
    <AddressDisplay
      address={address}
      leadChars={leadChars}
      tailChars={tailChars}
      fontSize={fontSize}
    />
  );

  if (resolved) {
    return (
      <Tooltip content={`${resolved.label} \u00b7 ${address}`}>
        <button
          type="button"
          onClick={() => navigate(resolved.route)}
          className={`decoration-[var(--color-text-muted)]/30 cursor-pointer underline underline-offset-2 transition-colors hover:decoration-[var(--color-gold)] ${className ?? ""}`}
        >
          {inner}
        </button>
      </Tooltip>
    );
  }

  return (
    <Tooltip content={address}>
      <span className={className}>{inner}</span>
    </Tooltip>
  );
}
